/**
 * ONE Create Ticket action = ONE operation identity, durable across an
 * OptiPlace close/reopen and an ambiguous network result.
 *
 * A create can commit on the server while the browser loses the answer (a
 * gateway timeout, a dropped connection). If the retry minted a new ticket
 * id, the second confirm would create a second root ticket and a second
 * rollover chain. So the id of the ticket a Create Ticket action intends to
 * make is minted ONCE for that action and kept in sessionStorage until the
 * create is known to have succeeded:
 *
 *  - it IS the root ticket's primary key, and it seeds the rollover chain
 *    ids, so the database itself refuses a second copy — including two
 *    submissions in flight at once;
 *  - before creating, OptiPlace looks the root up; if the earlier attempt
 *    committed, the retry resolves to that ticket instead of making another.
 *
 * Bound to the action, not to the contents: it is cleared on success, so a
 * second, genuinely separate Create Ticket action gets a new id even when
 * its contents are identical. The stored fingerprint is a HASH of the
 * intended ticket (no customer, vehicle or contact data is stored); an
 * operation is reused only for the same intended ticket, so a different
 * ticket typed after an abandoned attempt is a new operation.
 */
const STORAGE_KEY = 'optibay:create-ticket-operation:v1';
/** How long an unfinished operation may be reconciled. */
export const CREATE_OPERATION_TTL_MS = 12 * 60 * 60 * 1000;

export interface CreateTicketOperation {
  /** The root ticket's id. */
  id: string;
  /** Hash of the intended ticket. */
  fingerprint: string;
  /** Seeds the rollover continuation ids (stable across retries). */
  chainNowMs: number;
  /** A vehicle this operation already created, reused on retry. */
  vehicleId: string | null;
  mintedAtMs: number;
}

type Storage = Pick<globalThis.Storage, 'getItem' | 'setItem' | 'removeItem'>;
const defaultStorage = (): Storage | null => {
  try { return typeof sessionStorage === 'undefined' ? null : sessionStorage; } catch { return null; }
};

/** FNV-1a over a stable JSON rendering — identity only, not security. */
export function fingerprintCreateIntent(intent: unknown): string {
  const stable = (v: unknown): unknown => {
    if (Array.isArray(v)) return v.map(stable);
    if (v && typeof v === 'object') {
      return Object.fromEntries(Object.keys(v as object).sort().map((k) => [k, stable((v as Record<string, unknown>)[k])]));
    }
    return v ?? null;
  };
  const text = JSON.stringify(stable(intent));
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) { h ^= text.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
  return h.toString(16).padStart(8, '0');
}

function read(storage: Storage | null): CreateTicketOperation | null {
  try {
    const raw = storage?.getItem(STORAGE_KEY);
    if (!raw) return null;
    const op = JSON.parse(raw) as CreateTicketOperation;
    return op && typeof op.id === 'string' && typeof op.fingerprint === 'string' ? op : null;
  } catch { return null; }
}
function write(storage: Storage | null, op: CreateTicketOperation): void {
  try { storage?.setItem(STORAGE_KEY, JSON.stringify(op)); } catch { /* storage unavailable: in-memory only */ }
}

/**
 * The operation for this intended ticket: the unfinished one when it is the
 * same intent and not expired, otherwise a new one.
 */
export function getOrMintCreateOperation(
  fingerprint: string,
  opts: { storage?: Storage | null; nowMs?: number; newId?: () => string } = {},
): CreateTicketOperation {
  const storage = opts.storage === undefined ? defaultStorage() : opts.storage;
  const now = opts.nowMs ?? Date.now();
  const existing = read(storage);
  if (existing && existing.fingerprint === fingerprint && now - existing.mintedAtMs < CREATE_OPERATION_TTL_MS) {
    return existing;
  }
  const op: CreateTicketOperation = {
    id: (opts.newId ?? (() => crypto.randomUUID()))(),
    fingerprint,
    chainNowMs: now,
    vehicleId: null,
    mintedAtMs: now,
  };
  write(storage, op);
  return op;
}

/** The unfinished operation with this id, if it is still stored. */
export function readCreateOperation(id: string, storage: Storage | null = defaultStorage()): CreateTicketOperation | null {
  const op = read(storage);
  return op && op.id === id ? op : null;
}

/** Record the vehicle this operation created, so a retry does not create another. */
export function rememberCreateOperationVehicle(id: string, vehicleId: string, storage: Storage | null = defaultStorage()): void {
  const op = read(storage);
  if (op && op.id === id) write(storage, { ...op, vehicleId });
}

/** The operation is finished (created, or resolved to the original): forget it. */
export function clearCreateOperation(id: string, storage: Storage | null = defaultStorage()): void {
  const op = read(storage);
  if (op && op.id === id) {
    try { storage?.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  }
}
